/**
 * Combined Invoice Page
 * 
 * A simple wrapper that shows BOTH existing forms (Services + Pharmacy) 
 * using tabs to switch between them. No new form logic - just reuses
 * NewInvoiceForm and NewPharmacyForm as-is.
 */

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useLocation } from 'react-router-dom';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import InvoiceCategoryTabs from '../../../../components/Billing/Invoice/InvoiceCategoryTabs';

// Existing forms - reused as-is
import NewInvoiceForm from './NewInvoiceForm';
import NewPharmacyForm from '../../../Billing/Pharmacy/newPharmacy/NewPharmacyForm';

type TabType = 'services' | 'pharmacy';

const CombinedInvoicePage: React.FC = () => {
  const location = useLocation();
  
  // Show tabs only when navigating from InvoicePatientSelector (showTabs: true in state)
  // Hide tabs when coming from "By Services" tab (fromByServices: true in state)
  // Hide tabs when pharmacyOnly is true (coming from New Pharmacy Patient modal)
  const {
    showTabs = false,
    fromByServices = false,
    fromByPharmacy = false,
    pharmacyOnly = false,
  } = (location.state as { showTabs?: boolean; fromByServices?: boolean; fromByPharmacy?: boolean; pharmacyOnly?: boolean }) ?? {};
  const shouldShowTabs = showTabs && !fromByServices && !pharmacyOnly;

  const [activeTab, setActiveTab] = useState<TabType>(pharmacyOnly || fromByPharmacy ? 'pharmacy' : 'services');

  // Sync activeTab when location.state changes (e.g. navigating between patients)
  useEffect(() => {
    setActiveTab(pharmacyOnly || fromByPharmacy ? 'pharmacy' : 'services');
  }, [pharmacyOnly, fromByPharmacy]);


  // Tab categories for InvoiceCategoryTabs
  const categories = [
    { name: 'By Services', label: 'By Services', count: 0 },
    { name: 'By Pharmacy', label: 'By Pharmacy', count: 0 },
  ];

  const handleTabChange = (category: { name: string }) => {
    const tabName = category.name.toLowerCase();
    if (tabName.includes('service')) {
      setActiveTab('services');
    } else if (tabName.includes('pharmacy')) {
      setActiveTab('pharmacy');
    }
  };

  return (
    <>
      <HeaderProfile />

      <Box sx={{ mx: 2, mt: 4 }}>
        {/* Header: Title + Tab switcher on same line - shown only when from InvoicePatientSelector and not from "By Services" tab */}
        {shouldShowTabs && (
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={3} 
            sx={{ mb: 3 }}
          >
            <InvoiceCategoryTabs
              activeCategory={activeTab === 'services' ? 'By Services' : 'By Pharmacy'}
              categories={categories}
              onCategoryChange={handleTabChange}
              size="medium"
            />
          </Stack>
        )}

        {/* Conditionally render existing forms based on active tab */}
        {pharmacyOnly || activeTab === 'pharmacy' ? (
          <NewPharmacyForm />
        ) : (
          <NewInvoiceForm />
        )}
      </Box>
    </>
  );
};

export default CombinedInvoicePage;
